import React, { PropsWithChildren } from 'react';
import { VisibilityContext } from '../../vendor/react-horizontal-scrolling-menu';
import styled, { css } from 'styled-components';
import { IGutterAsMarginProps } from '../../fundamentals/spaces/space';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { Icon } from '../Icon/Icon';
import { MATERIAL_ICONS } from '../Icon/icons';

export enum ArrowOrientation {
  LEFT = 'Left',
  RIGHT = 'Right'
}

export enum ArrowState {
  REMOVED = 'Removed',
  DISABLED = 'Disabled',
  VISIBLE = 'Visible'
}

export interface IArrowProps extends IGutterAsMarginProps, React.HTMLAttributes<HTMLDivElement> {
  orientation: ArrowOrientation;
  disabled?: boolean;
  autoHide?: boolean;
  arrowState?: ArrowState;
}

const calcArrowState = (autoHide: boolean | undefined, disabled: boolean, allItemsVisible: boolean): ArrowState => {
  if (allItemsVisible && autoHide) return ArrowState.REMOVED;
  if (disabled) return ArrowState.DISABLED;
  return ArrowState.VISIBLE;
};

const StyledArrow = styled(GapContainer)<IArrowProps>`
  align-items: center;
  cursor: pointer;
  justify-content: center;
  user-select: none;
  width: initial;
  ${({ arrowState }) => {
    if (arrowState === ArrowState.DISABLED)
      return css`
        opacity: 0.25;
      `;
    if (arrowState === ArrowState.REMOVED)
      return css`
        display: none;
      `;
  }};
`;

const Arrow: React.FC<IArrowProps> = ({ orientation, children, autoHide, onClick }) => {
  const { isFirstItemVisible, isLastItemVisible, visibleElements, initComplete } = React.useContext(VisibilityContext);
  const [disabled, setDisabled] = React.useState(!initComplete || (initComplete && isFirstItemVisible));
  const [allItemsVisible, setAllItemsVisible] = React.useState(!initComplete || (initComplete && isFirstItemVisible && isLastItemVisible));

  const [arrowState, setArrowState] = React.useState(calcArrowState(autoHide, disabled, allItemsVisible));

  React.useEffect(() => {
    if (visibleElements?.length > 0) {
      setDisabled(orientation === ArrowOrientation.LEFT ? isFirstItemVisible : isLastItemVisible);
      setAllItemsVisible(isFirstItemVisible && isLastItemVisible);
    }
  }, [visibleElements]);

  React.useEffect(() => {
    setArrowState(calcArrowState(autoHide, disabled, allItemsVisible));
  }, [disabled, allItemsVisible]);

  return (
    <StyledArrow className='horizontal-scroll-styled-arrow' orientation={orientation} disabled={disabled} arrowState={arrowState} onClick={onClick} column>
      {children}
    </StyledArrow>
  );
};

export const LeftArrow: React.FC<
  PropsWithChildren<{
    autoHide?: boolean;
  }>
> = ({ autoHide, ...rest }) => {
  const { scrollPrev } = React.useContext(VisibilityContext);
  return (
    <Arrow orientation={ArrowOrientation.LEFT} autoHide={autoHide} onClick={() => scrollPrev()} {...rest}>
      <Icon name={MATERIAL_ICONS.ChevronLeft} />
    </Arrow>
  );
};

export const RightArrow: React.FC<
  PropsWithChildren<{
    autoHide?: boolean;
  }>
> = ({ autoHide, ...rest }) => {
  const { scrollNext } = React.useContext(VisibilityContext);
  return (
    <Arrow orientation={ArrowOrientation.RIGHT} autoHide={autoHide} onClick={() => scrollNext()} {...rest}>
      <Icon name={MATERIAL_ICONS.ChevronRight} />
    </Arrow>
  );
};
